import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import CandleChart from "@/components/CandleChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api, fmtErr, fmtUsd, fmtPrice, fmtDate } from "@/lib/api";
import { usePrices } from "@/lib/usePrices";
import StatusBadgeBase from "@/components/StatusBadge";
import NotificationBell from "@/components/NotificationBell";
import ChatWidget from "@/components/ChatWidget";
import { WalletTab, VerificationTab, DocumentsTab, FiscalTab } from "@/pages/client/ClientTabs";
import {
  LogOut, Globe, Wallet, Landmark, Hourglass, ShieldX,
  TrendingUp, TrendingDown, Trash2, ArrowDownRight, ArrowUpRight,
  LayoutDashboard, CandlestickChart, LineChart, ShieldCheck,
  FolderOpen, Scale, History,
} from "lucide-react";

const NAV_TABS = [
  { value: "overview", labelKey: "dash.overview", icon: LayoutDashboard, color: "#635BFF" },
  { value: "trading", labelKey: "dash.trading", icon: CandlestickChart, color: "#0073E6" },
  { value: "funding", labelKey: "dash.funding", icon: Landmark, color: "#00B8A9" },
  { value: "markets", labelKey: "dash.markets", icon: LineChart, color: "#FF5996" },
  { value: "wallet", labelKey: "wallet.tab", icon: Wallet, color: "#10B981" },
  { value: "verification", labelKey: "kyc.tab", icon: ShieldCheck, color: "#F5A623" },
  { value: "documents", labelKey: "docs.tab", icon: FolderOpen, color: "#7C5CD6" },
  { value: "fiscal", labelKey: "fiscal.tab", icon: Scale, color: "#0A2540" },
  { value: "history", labelKey: "dash.history", icon: History, color: "#64748B" },
];

const SYMBOLS = ["BTCUSDT","ETHUSDT","BNBUSDT","SOLUSDT","XRPUSDT","DOGEUSDT","ADAUSDT","TRXUSDT","LINKUSDT","LTCUSDT"];
const INTERVALS = ["1m", "15m", "1h", "4h", "1d", "1w"];

const cardCls = "bg-white border border-[#E6EBF1] rounded-2xl shadow-[0_8px_30px_rgba(10,37,64,0.05)]";
const inputCls = "mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40";
const labelCls = "text-[#425466] text-xs font-medium";
const selectContentCls = "bg-white border border-[#E6EBF1] text-[#0A2540] shadow-xl";
const selectItemCls = "focus:bg-[#F6F9FC] focus:text-[#0A2540] cursor-pointer";

export const StatusBadge = StatusBadgeBase;

function GateScreen({ status }) {
  const { t, lang, setLang } = useLang();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const conf = {
    pending: { icon: Hourglass, title: t("dash.pendingGateTitle"), body: t("dash.pendingGateBody"), color: "text-amber-500" },
    rejected: { icon: ShieldX, title: t("dash.rejectedGateTitle"), body: t("dash.rejectedGateBody"), color: "text-red-500" },
    suspended: { icon: ShieldX, title: t("dash.suspendedGateTitle"), body: t("dash.suspendedGateBody"), color: "text-red-500" },
  }[status] || {};
  const Icon = conf.icon || Hourglass;
  return (
    <div className="min-h-screen bg-[#F6F9FC] flex flex-col items-center justify-center px-5" data-testid="pending-gate">
      <div className={`${cardCls} p-10 max-w-md text-center`}>
        <Icon size={44} className={`${conf.color} mx-auto mb-5`} />
        <h1 className="font-display text-xl font-bold text-[#0A2540]">{conf.title}</h1>
        <p className="text-sm text-[#425466] mt-3 leading-relaxed">{conf.body}</p>
        <div className="mt-6 flex justify-center">
          <StatusBadge status={status === "active" ? "validated" : status} />
        </div>
        <div className="flex items-center justify-center gap-3 mt-8">
          <button onClick={() => setLang(lang === "en" ? "fr" : "en")}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#425466] border border-[#E6EBF1] bg-white rounded-full px-3 py-1.5">
            <Globe size={12} /> {lang === "en" ? "FR" : "EN"}
          </button>
          <Button variant="outline" size="sm" data-testid="gate-logout-btn"
                  className="border-[#E6EBF1] text-[#425466] hover:bg-[#F6F9FC] rounded-full"
                  onClick={async () => { await logout(); navigate("/"); }}>
            <LogOut size={14} className="mr-1.5" /> {t("dash.logout")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TradeForm({ prices, onDone }) {
  const { t } = useLang();
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const live = prices[symbol];

  const submit = async (e) => {
    e.preventDefault();
    if (!live) return;
    setLoading(true);
    try {
      await api.post("/client/trades", {
        symbol, side, amount_usd: parseFloat(amount), price: live.price,
      });
      toast.success(t("dash.tradeSent"));
      setAmount("");
      onDone();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${cardCls} p-5 space-y-4`} data-testid="trade-form">
      <h3 className="font-display text-base font-semibold text-[#0A2540]">{t("dash.tradeTicket")}</h3>
      <div className="grid grid-cols-2 gap-2">
        {["buy", "sell"].map((s) => (
          <button
            key={s} type="button" data-testid={`trade-side-${s}`}
            onClick={() => setSide(s)}
            className={`h-10 rounded-lg text-sm font-semibold transition-all border ${
              side === s
                ? s === "buy"
                  ? "bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-600/25"
                  : "bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/25"
                : "border-[#E6EBF1] text-[#425466] hover:bg-[#F6F9FC]"
            }`}
          >
            {s === "buy" ? t("dash.buy") : t("dash.sell")}
          </button>
        ))}
      </div>
      <div>
        <Label className={labelCls}>{t("dash.symbol")}</Label>
        <Select value={symbol} onValueChange={setSymbol}>
          <SelectTrigger data-testid="trade-symbol-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={selectContentCls}>
            {SYMBOLS.map((s) => (
              <SelectItem key={s} value={s} className={selectItemCls}>{s.replace("USDT", "")}/USDT</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={labelCls}>{t("dash.amount")}</Label>
        <Input
          data-testid="trade-amount-input" type="number" min="1" step="any" required
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className={`${inputCls} font-ticker`}
          placeholder="1000.00"
        />
      </div>
      <div className="flex justify-between text-xs font-ticker text-[#425466]">
        <span>{t("dash.price")}</span>
        <span className="text-emerald-600 font-semibold">${live ? fmtPrice(live.price) : "…"}</span>
      </div>
      {amount && live && (
        <div className="flex justify-between text-xs font-ticker text-[#425466]">
          <span>{t("dash.quantity")}</span>
          <span className="text-[#0A2540] font-semibold">{(parseFloat(amount) / live.price).toFixed(8)} {symbol.replace("USDT", "")}</span>
        </div>
      )}
      <Button data-testid="trade-submit-btn" type="submit" disabled={loading || !live}
              className={`w-full h-11 font-semibold text-white rounded-full transition-colors ${side === "buy" ? "bg-emerald-600 hover:bg-[#0A2540]" : "bg-red-500 hover:bg-[#0A2540]"}`}>
        {loading ? t("common.loading") : t("dash.submitTrade")}
      </Button>
      <p className="text-[11px] text-slate-400 leading-relaxed">{t("dash.fundingNote")}</p>
    </form>
  );
}

export function TradesTable({ trades, showOrigin = true }) {
  const { t } = useLang();
  if (!trades.length)
    return <p className="text-sm text-slate-400 py-8 text-center" data-testid="no-trades">{t("dash.noTrades")}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]" data-testid="trades-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-[#E6EBF1] bg-[#F6F9FC]">
            <th className="text-left px-4 py-3">{t("dash.time")}</th>
            <th className="text-left px-4 py-3">{t("dash.symbol")}</th>
            <th className="text-left px-4 py-3">{t("dash.side")}</th>
            <th className="text-right px-4 py-3">{t("dash.amount")}</th>
            <th className="text-right px-4 py-3">{t("dash.price")}</th>
            <th className="text-right px-4 py-3">{t("dash.quantity")}</th>
            {showOrigin && <th className="text-left px-4 py-3">{t("dash.by")}</th>}
            <th className="text-left px-4 py-3">{t("dash.status")}</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((tr) => (
            <tr key={tr.id} className="border-b border-[#E6EBF1] hover:bg-[#F6F9FC] transition-colors">
              <td className="px-4 py-3 text-[#425466] text-xs font-ticker">{fmtDate(tr.created_at)}</td>
              <td className="px-4 py-3 font-semibold text-[#0A2540]">{tr.symbol.replace("USDT", "")}/USDT</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tr.side === "buy" ? "text-emerald-600" : "text-red-500"}`}>
                  {tr.side === "buy" ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                  {tr.side.toUpperCase()}
                </span>
              </td>
              <td className="px-4 py-3 text-right font-ticker font-semibold text-[#0A2540]">{fmtUsd(tr.amount_usd)}</td>
              <td className="px-4 py-3 text-right font-ticker text-[#425466]">${fmtPrice(tr.price)}</td>
              <td className="px-4 py-3 text-right font-ticker text-[#425466]">{tr.quantity?.toFixed(8)}</td>
              {showOrigin && (
                <td className="px-4 py-3 text-xs text-[#425466]">
                  {tr.created_by === "admin" ? t("dash.byDesk") : t("dash.byMe")}
                </td>
              )}
              <td className="px-4 py-3"><StatusBadge status={tr.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Funding() {
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const [bank, setBank] = useState({ iban: "", bank_name: "", bic: "" });
  const [wallet, setWallet] = useState({ asset: "USDC", network: "Base", address: "", label: "" });

  const load = () => api.get("/client/funding").then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitBank = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/funding/bank", bank);
      toast.success(t("dash.bankAdded"));
      setBank({ iban: "", bank_name: "", bic: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submitWallet = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/funding/wallet", wallet);
      toast.success(t("dash.walletAdded"));
      setWallet({ asset: "USDC", network: "Base", address: "", label: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/client/funding/${id}`); load(); } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="funding-tab">
      <div className="space-y-6">
        <form onSubmit={submitBank} className={`${cardCls} p-6 space-y-4`} data-testid="bank-form">
          <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
            <Landmark size={17} className="text-[#635BFF]" /> {t("dash.addBank")}
          </h3>
          <div>
            <Label className={labelCls}>{t("dash.iban")}</Label>
            <Input data-testid="bank-iban-input" value={bank.iban} required placeholder="FR76 3000 4028 3798 7654 3210 985"
                   onChange={(e) => setBank({ ...bank, iban: e.target.value })} className={`${inputCls} font-ticker`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>{t("dash.bankName")}</Label>
              <Input data-testid="bank-name-input" value={bank.bank_name} required
                     onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>{t("dash.bic")}</Label>
              <Input data-testid="bank-bic-input" value={bank.bic} required placeholder="BNPAFRPP"
                     onChange={(e) => setBank({ ...bank, bic: e.target.value })} className={`${inputCls} font-ticker`} />
            </div>
          </div>
          <Button data-testid="bank-submit-btn" type="submit" className="bg-[#635BFF] hover:bg-[#0A2540] text-white w-full h-10 font-semibold rounded-full transition-colors">
            {t("dash.addBankBtn")}
          </Button>
        </form>

        <form onSubmit={submitWallet} className={`${cardCls} p-6 space-y-4`} data-testid="wallet-form">
          <h3 className="font-display text-base font-semibold text-[#0A2540] flex items-center gap-2">
            <Wallet size={17} className="text-[#635BFF]" /> {t("dash.addWallet")}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>{t("dash.walletAsset")}</Label>
              <Select value={wallet.asset} onValueChange={(v) => setWallet({ ...wallet, asset: v })}>
                <SelectTrigger data-testid="wallet-asset-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {["USDC", "USDT", "BTC", "ETH", "SOL"].map((a) => <SelectItem key={a} value={a} className={selectItemCls}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>{t("dash.walletNetwork")}</Label>
              <Select value={wallet.network} onValueChange={(v) => setWallet({ ...wallet, network: v })}>
                <SelectTrigger data-testid="wallet-network-select" className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {["Base", "ERC-20", "TRC-20", "BEP-20", "Solana", "Bitcoin"].map((n) => <SelectItem key={n} value={n} className={selectItemCls}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className={labelCls}>{t("dash.walletAddress")}</Label>
            <Input data-testid="wallet-address-input" value={wallet.address} required placeholder="0x…"
                   onChange={(e) => setWallet({ ...wallet, address: e.target.value })} className={`${inputCls} font-ticker`} />
          </div>
          <div>
            <Label className={labelCls}>{t("dash.walletLabel")}</Label>
            <Input data-testid="wallet-label-input" value={wallet.label}
                   onChange={(e) => setWallet({ ...wallet, label: e.target.value })} className={inputCls} />
          </div>
          <Button data-testid="wallet-submit-btn" type="submit" className="bg-[#635BFF] hover:bg-[#0A2540] text-white w-full h-10 font-semibold rounded-full transition-colors">
            {t("dash.addWalletBtn")}
          </Button>
        </form>
      </div>

      <div className={`${cardCls} p-6`} data-testid="funding-list">
        <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("dash.myFunding")}</h3>
        <p className="text-xs text-slate-400 mb-5 leading-relaxed">{t("dash.fundingNote")}</p>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="no-funding">{t("dash.noFunding")}</p>
        ) : (
          <div className="space-y-3">
            {entries.map((en) => (
              <div key={en.id} className="border border-[#E6EBF1] rounded-xl p-4 flex items-start justify-between gap-3 bg-[#F6F9FC] hover:border-[#635BFF]/40 transition-colors"
                   data-testid={`funding-entry-${en.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {en.type === "bank" ? <Landmark size={14} className="text-[#635BFF]" /> : <Wallet size={14} className="text-[#635BFF]" />}
                    <span className="text-sm font-semibold text-[#0A2540]">
                      {en.type === "bank" ? en.bank_name : `${en.asset} — ${en.network}`}
                    </span>
                    <StatusBadge status={en.status} />
                  </div>
                  <p className="text-xs font-ticker text-[#425466] mt-1.5 break-all">
                    {en.type === "bank" ? en.iban : en.address}
                  </p>
                  {en.type === "bank" && <p className="text-xs text-slate-400 mt-0.5 font-ticker">{en.bic}</p>}
                  {en.label && <p className="text-xs text-slate-400 mt-0.5">{en.label}</p>}
                </div>
                {en.status === "pending" && (
                  <button onClick={() => remove(en.id)} data-testid={`funding-delete-${en.id}`}
                          className="text-slate-400 hover:text-red-500 transition-colors shrink-0">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MarketsPanel() {
  const { t } = useLang();
  const { prices, live } = usePrices();
  const items = SYMBOLS.map((s) => prices[s]).filter(Boolean);
  return (
    <div className={`${cardCls} overflow-hidden overflow-x-auto`} data-testid="dash-markets-panel">
      <div className="flex items-center justify-between px-6 pt-5">
        <h3 className="font-display text-base font-semibold text-[#0A2540]">{t("dash.markets")}</h3>
        <div className="flex items-center gap-2 text-xs font-ticker text-emerald-600">
          <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse-dot" : "bg-amber-400"}`} />
          LIVE · BINANCE
        </div>
      </div>
      <table className="w-full text-sm min-w-[680px] mt-3" data-testid="dash-markets-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-[#E6EBF1] bg-[#F6F9FC]">
            <th className="text-left px-6 py-3">{t("dash.symbol")}</th>
            <th className="text-right px-6 py-3">{t("dash.price")}</th>
            <th className="text-right px-6 py-3">24h</th>
            <th className="text-right px-6 py-3">24h High</th>
            <th className="text-right px-6 py-3">24h Low</th>
          </tr>
        </thead>
        <tbody>
          {items.map((tk) => (
            <tr key={tk.symbol} className="border-b border-[#E6EBF1] hover:bg-[#F6F9FC] transition-colors">
              <td className="px-6 py-3.5 font-semibold text-[#0A2540]">{tk.name}<span className="text-slate-400 text-xs">/USDT</span></td>
              <td className="px-6 py-3.5 text-right font-ticker font-semibold text-[#0A2540]">${fmtPrice(tk.price)}</td>
              <td className={`px-6 py-3.5 text-right font-ticker ${tk.changePct >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {tk.changePct >= 0 ? "+" : ""}{tk.changePct.toFixed(2)}%
              </td>
              <td className="px-6 py-3.5 text-right font-ticker text-slate-500">${fmtPrice(tk.high)}</td>
              <td className="px-6 py-3.5 text-right font-ticker text-slate-500">${fmtPrice(tk.low)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const { prices, live } = usePrices();
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [chartSymbol, setChartSymbol] = useState("BTCUSDT");
  const [interval, setChartInterval] = useState("1h");

  const isActive = user && user.status === "active";

  const load = () => {
    if (!isActive) return;
    api.get("/client/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/client/trades").then((r) => setTrades(r.data)).catch(() => {});
  };
  useEffect(load, [isActive]);

  const btcEquiv = useMemo(() => {
    if (!portfolio || !prices["BTCUSDT"]) return null;
    return portfolio.total_usd / prices["BTCUSDT"].price;
  }, [portfolio, prices]);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role === "admin") navigate("/ucp-admin");
  }, [user, navigate]);

  if (user === null)
    return <div className="min-h-screen bg-[#F6F9FC] flex items-center justify-center text-[#425466] text-sm">{t("common.loading")}</div>;
  if (user === false || user.role === "admin") return null;
  if (!isActive) return <GateScreen status={user.status} />;

  const cash = portfolio?.balances.find((b) => b.asset === "USD");

  return (
    <div className="min-h-screen bg-[#F6F9FC]" data-testid="client-dashboard">
      <header className="bg-white/85 backdrop-blur-xl border-b border-[#E6EBF1] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 h-16 flex items-center justify-between">
          <Logo light />
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs font-ticker text-emerald-600">
              <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse-dot" : "bg-amber-400"}`} />
              LIVE
            </div>
            <NotificationBell />
            <button onClick={() => setLang(lang === "en" ? "fr" : "en")} data-testid="dash-lang-toggle"
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#425466] border border-[#E6EBF1] bg-white rounded-full px-3 py-1.5 hover:text-[#0A2540]">
              <Globe size={12} /> {lang === "en" ? "FR" : "EN"}
            </button>
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-[#0A2540]">{user.full_name}</p>
              <p className="text-[10px] text-slate-400">{user.email}</p>
            </div>
            <Button variant="outline" size="sm" data-testid="dash-logout-btn"
                    className="border-[#E6EBF1] text-[#425466] hover:bg-[#F6F9FC] rounded-full"
                    onClick={async () => { await logout(); navigate("/"); }}>
              <LogOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="portfolio-summary">
          <div className={`${cardCls} p-5 border-l-4 border-l-[#635BFF]`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t("dash.totalValue")}</p>
            <p className="font-ticker text-2xl font-semibold text-[#0A2540] mt-1.5" data-testid="total-value">
              {portfolio ? fmtUsd(portfolio.total_usd) : "…"}
            </p>
          </div>
          <div className={`${cardCls} p-5 border-l-4 border-l-emerald-500`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t("dash.cash")}</p>
            <p className="font-ticker text-2xl font-semibold text-emerald-600 mt-1.5" data-testid="cash-value">
              {cash ? fmtUsd(cash.amount) : "…"}
            </p>
          </div>
          <div className={`${cardCls} p-5 border-l-4 border-l-[#FF5996]`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t("dash.btcEq")}</p>
            <p className="font-ticker text-2xl font-semibold text-[#0A2540] mt-1.5" data-testid="btc-equiv">
              {btcEquiv != null ? `₿ ${btcEquiv.toFixed(8)}` : "…"}
            </p>
          </div>
        </div>

        <Tabs defaultValue="trading">
          <TabsList className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3 bg-transparent border-0 p-0 h-auto" data-testid="dash-tabs">
            {NAV_TABS.map((tab, i) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`tab-${tab.value}`}
                  style={{ "--c": tab.color, animationDelay: `${i * 60}ms` }}
                  className="group relative flex flex-col items-center justify-center gap-2 rounded-2xl border border-[#E6EBF1] bg-white px-2 py-4 text-[#425466] whitespace-normal shadow-none animate-tab-tile transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--c)] hover:shadow-[0_18px_38px_-14px_var(--c)] data-[state=active]:bg-white data-[state=active]:-translate-y-1.5 data-[state=active]:border-[var(--c)] data-[state=active]:shadow-[0_18px_38px_-14px_var(--c)] data-[state=active]:text-[var(--c)]"
                >
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6 group-data-[state=active]:scale-110"
                    style={{ backgroundColor: `${tab.color}1A`, color: tab.color }}
                  >
                    <Icon size={18} />
                  </span>
                  <span className="text-[11px] font-semibold leading-tight text-center">{t(tab.labelKey)}</span>
                  <span
                    className="absolute -bottom-px left-1/2 -translate-x-1/2 h-[3px] w-0 rounded-full transition-all duration-300 group-data-[state=active]:w-8"
                    style={{ backgroundColor: tab.color }}
                  />
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="overview" className="mt-6 animate-fade-up-fast">
            <div className={`${cardCls} p-6`} data-testid="allocation-panel">
              <h3 className="font-display text-base font-semibold text-[#0A2540] mb-5">{t("dash.allocTitle")}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {portfolio?.balances.map((b) => (
                  <div key={b.asset} className="border border-[#E6EBF1] rounded-xl p-4 bg-[#F6F9FC] hover:border-[#635BFF]/40 hover:-translate-y-0.5 transition-all duration-300" data-testid={`balance-${b.asset}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#0A2540] text-sm">{b.asset}</span>
                      {b.asset !== "USD" && prices[`${b.asset}USDT`] && (
                        prices[`${b.asset}USDT`].changePct >= 0
                          ? <TrendingUp size={14} className="text-emerald-500" />
                          : <TrendingDown size={14} className="text-red-500" />
                      )}
                    </div>
                    <p className="font-ticker text-lg text-[#0A2540] mt-2">{b.amount.toFixed(b.asset === "USD" ? 2 : 8)}</p>
                    <p className="text-xs font-ticker text-slate-400 mt-0.5">{fmtUsd(b.value)}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={`${cardCls} p-6 mt-6`}>
              <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("dash.recentTrades")}</h3>
              <TradesTable trades={trades.slice(0, 5)} />
            </div>
          </TabsContent>

          <TabsContent value="trading" className="mt-6 animate-fade-up-fast">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className={`lg:col-span-2 ${cardCls} p-5`} data-testid="chart-panel">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <Select value={chartSymbol} onValueChange={setChartSymbol}>
                    <SelectTrigger data-testid="chart-symbol-select" className="w-40 bg-white border-[#E6EBF1] text-[#0A2540] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {SYMBOLS.map((s) => <SelectItem key={s} value={s} className={selectItemCls}>{s.replace("USDT", "")}/USDT</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    {INTERVALS.map((iv) => (
                      <button key={iv} data-testid={`interval-${iv}`} onClick={() => setChartInterval(iv)}
                              className={`px-3 h-8 rounded-md text-xs font-ticker transition-colors ${
                                interval === iv ? "bg-[#635BFF] text-white" : "text-[#425466] hover:bg-[#F6F9FC]"
                              }`}>
                        {iv}
                      </button>
                    ))}
                  </div>
                </div>
                {prices[chartSymbol] && (
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-ticker text-2xl font-semibold text-[#0A2540]">${fmtPrice(prices[chartSymbol].price)}</span>
                    <span className={`font-ticker text-sm font-semibold ${prices[chartSymbol].changePct >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {prices[chartSymbol].changePct >= 0 ? "+" : ""}{prices[chartSymbol].changePct.toFixed(2)}%
                    </span>
                  </div>
                )}
                <CandleChart symbol={chartSymbol} interval={interval} />
              </div>
              <TradeForm prices={prices} onDone={load} />
            </div>
            <div className={`${cardCls} p-6 mt-6`}>
              <h3 className="font-display text-base font-semibold text-[#0A2540] mb-4">{t("dash.recentTrades")}</h3>
              <TradesTable trades={trades} />
            </div>
          </TabsContent>

          <TabsContent value="funding" className="mt-6 animate-fade-up-fast"><Funding /></TabsContent>

          <TabsContent value="markets" className="mt-6 animate-fade-up-fast"><MarketsPanel /></TabsContent>

          <TabsContent value="wallet" className="mt-6 animate-fade-up-fast"><WalletTab /></TabsContent>

          <TabsContent value="verification" className="mt-6 animate-fade-up-fast"><VerificationTab /></TabsContent>

          <TabsContent value="documents" className="mt-6 animate-fade-up-fast"><DocumentsTab /></TabsContent>

          <TabsContent value="fiscal" className="mt-6 animate-fade-up-fast"><FiscalTab /></TabsContent>

          <TabsContent value="history" className="mt-6 animate-fade-up-fast">
            <div className={`${cardCls} p-6`} data-testid="history-panel">
              <h3 className="font-display text-base font-semibold text-[#0A2540]">{t("dash.recentTrades")}</h3>
              <p className="text-xs text-slate-400 mt-1 mb-4">{t("dash.historyNote")}</p>
              <TradesTable trades={trades} />
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <ChatWidget />
    </div>
  );
}
